import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import MkdSDK from "@/utils/MkdSDK";
import { GlobalContext, showToast } from "@/context/Global";
import { AuthContext, tokenExpireError } from "@/context/Auth";
import { LazyLoad } from "@/components/LazyLoad";
import { MkdInput } from "@/components/MkdInput";
import { InteractiveButton } from "@/components/InteractiveButton";
import { ArrowLeft } from "lucide-react";

interface Product {
  id: string;
  name: string;
}

interface PriceFormData {
  product_id: string;
  name: string;
  amount: number;
  type: string;
  interval?: string;
  interval_count?: number;
  trial_days?: number;
  pay_per_usage?: boolean;
  pay_per_seat?: boolean;
  is_usage_metered?: boolean;
  usage_limit?: number;
}

interface AddAdminPricePageProps {
  onClose: () => void;
  onSuccess: () => void;
}

const schema = yup.object().shape({
  product_id: yup.string().required("Product is required"),
  name: yup.string().required("Name is required"),
  amount: yup
    .number()
    .required("Amount is required")
    .positive("Amount must be positive")
    .test(
      "is-decimal",
      "Amount must have at most 2 decimal places",
      (value) => {
        if (!value) return true;
        return /^\d+(\.\d{0,2})?$/.test(value.toString());
      }
    ),
  type: yup.string().required("Type is required"),
  interval: yup.string().when("type", {
    is: "recurring",
    then: (schema) => schema.required("Interval is required"),
    otherwise: (schema) => schema.optional(),
  }),
  interval_count: yup.number().when("type", {
    is: "recurring",
    then: (schema) =>
      schema
        .required("Interval count is required")
        .positive("Interval count must be positive")
        .integer("Interval count must be an integer"),
    otherwise: (schema) => schema.optional(),
  }),
  trial_days: yup
    .number()
    .optional()
    .min(0, "Trial days must be non-negative")
    .integer("Trial days must be an integer"),
  pay_per_usage: yup.boolean().optional(),
  pay_per_seat: yup.boolean().optional(),
  is_usage_metered: yup.boolean().optional(),
  usage_limit: yup
    .number()
    .optional()
    .when("is_usage_metered", {
      is: true,
      then: (schema) =>
        schema
          .positive("Usage limit must be positive")
          .integer("Usage limit must be an integer"),
      otherwise: (schema) => schema.optional(),
    }),
});

const AddAdminPricePage: React.FC<AddAdminPricePageProps> = ({
  onClose,
  onSuccess,
}) => {
  const { dispatch: globalDispatch } = React.useContext(GlobalContext);
  const { dispatch: authDispatch } = React.useContext(AuthContext);
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(true);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<PriceFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      type: "recurring",
      interval: "month",
      interval_count: 1,
      trial_days: 0,
      pay_per_usage: false,
      pay_per_seat: false,
      is_usage_metered: false,
    },
  });

  const watchType = watch("type");
  const watchIsUsageMetered = watch("is_usage_metered");

  // Fetch products
  useEffect(() => {
    const fetchProducts = async () => {
      setLoadingProducts(true);
      try {
        const sdk = new MkdSDK();
        const result = await sdk.callRawAPI(
          "/v1/api/contentgap/customer/lambda/stripe/products",
          {},
          "GET"
        );

        if (!result.error) {
          setProducts(result.list || []);
        } else {
          showToast(globalDispatch, "Failed to load products", 5000, "error");
        }
      } catch (error: any) {
        console.error("Error fetching products:", error);
        tokenExpireError(authDispatch, error.message);
      } finally {
        setLoadingProducts(false);
      }
    };

    fetchProducts();
  }, [globalDispatch, authDispatch]);

  const onSubmit = async (data: PriceFormData) => {
    setIsSubmitting(true);
    try {
      const sdk = new MkdSDK();

      // Convert amount to cents
      const amountInCents = Math.round(data.amount * 100);

      // Prepare create data
      const createData = {
        product_id: parseInt(data.product_id, 10),
        name: data.name,
        amount: data.amount,
        type: data.type,
        pay_per_usage: data.pay_per_usage,
        pay_per_seat: data.pay_per_seat,
        usage_type: "licenced",
        ...(data.type === "recurring" && {
          interval: data.interval,
          interval_count: data.interval_count,
        }),
        trial_days: data.trial_days,
        ...(data.is_usage_metered && {
          usage_limit: data.usage_limit,
        }),
      };

      // Create the price
      const result = await sdk.callRawAPI(
        "/v1/api/contentgap/customer/lambda/stripe/price",
        createData,
        "POST"
      );

      if (!result.error) {
        showToast(
          globalDispatch,
          "Price created successfully",
          3000,
          "success"
        );
        onSuccess();
        // navigate(`/admin/prices/view/${result.list.id}`);
      } else {
        showToast(
          globalDispatch,
          result.message || "Failed to create price",
          3000,
          "error"
        );
      }
    } catch (error: any) {
      console.error("Error creating price:", error);
      showToast(
        globalDispatch,
        error.message || "An error occurred",
        3000,
        "error"
      );
      tokenExpireError(authDispatch, error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="h-full w-full overflow-auto p-6">
      <div className="mb-6 flex items-center">
        <InteractiveButton
          onClick={onClose}
          className="mr-4 !bg-transparent !p-2 !text-gray-600 hover:!bg-gray-100"
        >
          <ArrowLeft size={20} />
        </InteractiveButton>
        <h1 className="text-2xl font-bold text-gray-800">Create New Price</h1>
      </div>

      <div className="max-w-2xl rounded-lg bg-white p-6 shadow-md">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-4">
            {/* Product Selection */}
            <div>
              <LazyLoad>
                <MkdInput
                  type="select"
                  name="product_id"
                  label="Product"
                  register={register}
                  errors={errors}
                  required={true}
                  options={[
                    { value: "", label: "Select a product" },
                    ...products.map((product) => ({
                      value: product.id,
                      label: product.name,
                    })),
                  ]}
                  disabled={loadingProducts}
                />
              </LazyLoad>
              {loadingProducts && (
                <p className="mt-1 text-xs text-gray-500">
                  Loading products...
                </p>
              )}
            </div>

            {/* Price Name */}
            <div>
              <LazyLoad>
                <MkdInput
                  type="text"
                  name="name"
                  label="Price Name"
                  register={register}
                  errors={errors}
                  required={true}
                  placeholder="e.g., Basic Plan Monthly"
                />
              </LazyLoad>
            </div>

            {/* Amount */}
            <div>
              <LazyLoad>
                <MkdInput
                  type="number"
                  name="amount"
                  label="Amount"
                  register={register}
                  errors={errors}
                  required={true}
                  placeholder="Enter amount"
                />
              </LazyLoad>
              <p className="mt-1 text-xs text-gray-500">
                Enter the amount in dollars (e.g., 19.99 for $19.99)
              </p>
            </div>

            {/* Price Type */}
            <div>
              <LazyLoad>
                <MkdInput
                  type="select"
                  name="type"
                  label="Price Type"
                  register={register}
                  errors={errors}
                  required={true}
                  options={[
                    { value: "recurring", label: "Recurring" },
                    { value: "one_time", label: "One-time" },
                  ]}
                />
              </LazyLoad>
            </div>

            {/* Recurring Price Options */}
            {watchType === "recurring" && (
              <>
                <div>
                  <LazyLoad>
                    <MkdInput
                      type="select"
                      name="interval"
                      label="Billing Interval"
                      register={register}
                      errors={errors}
                      required={true}
                      options={[
                        { value: "day", label: "Daily" },
                        { value: "week", label: "Weekly" },
                        { value: "month", label: "Monthly" },
                        { value: "year", label: "Yearly" },
                      ]}
                    />
                  </LazyLoad>
                </div>

                <div>
                  <LazyLoad>
                    <MkdInput
                      type="number"
                      name="interval_count"
                      label="Interval Count"
                      register={register}
                      errors={errors}
                      required={true}
                    />
                  </LazyLoad>
                  <p className="mt-1 text-xs text-gray-500">
                    How many intervals between billings (e.g., 1 for monthly, 3
                    for quarterly)
                  </p>
                </div>
              </>
            )}

            {/* Trial Days */}
            <div>
              <LazyLoad>
                <MkdInput
                  type="number"
                  name="trial_days"
                  label="Trial Days"
                  register={register}
                  errors={errors}
                />
              </LazyLoad>
              <p className="mt-1 text-xs text-gray-500">
                Number of days for the trial period (0 for no trial)
              </p>
            </div>

            {/* Pay Per Usage */}
            <div>
              <LazyLoad>
                <MkdInput
                  type="toggle"
                  name="pay_per_usage"
                  label="Pay Per Usage"
                  register={register}
                  errors={errors}
                />
              </LazyLoad>
              <p className="mt-1 text-xs text-gray-500">
                Enable if customers pay based on usage
              </p>
            </div>

            {/* Pay Per Seat */}
            <div>
              <LazyLoad>
                <MkdInput
                  type="toggle"
                  name="pay_per_seat"
                  label="Pay Per Seat"
                  register={register}
                  errors={errors}
                />
              </LazyLoad>
              <p className="mt-1 text-xs text-gray-500">
                Enable if customers pay based on number of seats/users
              </p>
            </div>

            {/* Usage Metered */}
            <div>
              <LazyLoad>
                <MkdInput
                  type="toggle"
                  name="is_usage_metered"
                  label="Usage Metered"
                  register={register}
                  errors={errors}
                />
              </LazyLoad>
              <p className="mt-1 text-xs text-gray-500">
                Enable if this price has metered usage
              </p>
            </div>

            {/* Usage Limit (only if metered) */}
            {watchIsUsageMetered && (
              <div>
                <LazyLoad>
                  <MkdInput
                    type="number"
                    name="usage_limit"
                    label="Usage Limit"
                    register={register}
                    errors={errors}
                  />
                </LazyLoad>
                <p className="mt-1 text-xs text-gray-500">
                  Maximum usage allowed (leave empty for unlimited)
                </p>
              </div>
            )}
          </div>

          <div className="mt-6 flex justify-end space-x-3">
            <InteractiveButton
              onClick={onClose}
              className="!bg-gray-200 !text-gray-800 hover:!bg-gray-300"
            >
              Cancel
            </InteractiveButton>
            <InteractiveButton
              type="submit"
              disabled={isSubmitting}
              className="!bg-blue-600 hover:!bg-blue-700"
            >
              {isSubmitting ? "Creating..." : "Create Price"}
            </InteractiveButton>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddAdminPricePage;
